import { useEffect } from 'react'
import { CalendarDays, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'

import { useReleaseCampaign } from '@contexts/ReleaseCampaignContext'
import { useReleaseCampaignAnalytics } from '@hooks/useReleaseCampaignAnalytics'
import { RELEASE_CAMPAIGN_PLACEMENTS } from '@services/releaseCampaignAnalytics'
import { formatReleaseCampaignDate } from '@utils/releaseCampaign'

function ReleaseStatus({ campaign }) {
  if (campaign.status === 'planned') {
    const formattedReleaseDate = formatReleaseCampaignDate(campaign.releaseDate)

    if (!campaign.statusCopy.plannedPrefix || !formattedReleaseDate) {
      return null
    }

    return (
      <>
        {campaign.statusCopy.plannedPrefix}{' '}
        <time dateTime={campaign.releaseDate} className="font-semibold">
          {formattedReleaseDate}
        </time>
        .
      </>
    )
  }

  return campaign.statusCopy[campaign.status] || null
}

function ProductReleaseNotice() {
  const { campaign } = useReleaseCampaign()
  const { trackCtaClick, trackView } = useReleaseCampaignAnalytics(campaign)

  useEffect(() => {
    if (campaign.enabled) {
      trackView(RELEASE_CAMPAIGN_PLACEMENTS.PUBLIC_RELEASE_NOTICE)
    }
  }, [campaign.enabled, trackView])

  if (!campaign.enabled) return null

  const statusContent = <ReleaseStatus campaign={campaign} />

  return (
    <aside
      aria-labelledby="product-release-notice-title"
      data-release-status={campaign.status}
      className="border-b border-[var(--x3-border)] bg-[var(--x3-action-soft)]"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-1.5 px-4 py-2 sm:gap-2 sm:px-6 sm:py-2.5 lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:px-8">
        <div className="flex min-w-0 items-start gap-2.5 lg:flex-1 lg:items-center">
          <Lock
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--x3-control-accent)] lg:mt-0"
            aria-hidden="true"
          />
          <p className="min-w-0 text-xs leading-4 text-[var(--x3-text-muted)] [overflow-wrap:anywhere] sm:text-sm sm:leading-5">
            <span
              id="product-release-notice-title"
              className="mr-2 font-mono text-[10px] font-semibold uppercase text-[var(--x3-control-accent)] sm:text-xs"
            >
              {campaign.banner.label}
            </span>
            {campaign.banner.message}
          </p>
        </div>

        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-4 gap-y-1 pl-6 text-xs leading-4 sm:pl-7 sm:text-sm sm:leading-5 lg:max-w-[55%] lg:pl-0">
          <p className="flex min-w-0 items-center gap-2 text-[var(--x3-text)] [overflow-wrap:anywhere]">
            <CalendarDays
              className="h-4 w-4 shrink-0 text-[#0F9D78] dark:text-[#70D9BC]"
              aria-hidden="true"
            />
            <span>{statusContent}</span>
          </p>
          <Link
            to={campaign.primaryCta.path}
            onClick={() =>
              trackCtaClick(
                RELEASE_CAMPAIGN_PLACEMENTS.PUBLIC_RELEASE_NOTICE,
                campaign.primaryCta.path
              )
            }
            className="max-w-full font-semibold text-[var(--x3-link)] underline decoration-1 underline-offset-4 [overflow-wrap:anywhere] hover:text-[var(--x3-link-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--x3-action-focus)]"
          >
            {campaign.primaryCta.label}
          </Link>
        </div>
      </div>
    </aside>
  )
}

export default ProductReleaseNotice
